import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, History, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CheckLabel, Input, Modal, ModalActions, ModalList, ModalTitle, Overlay, Select, Textarea } from './ui';
import PersonPicker from './PersonPicker';
import { EmailViewSwitch, TemplateFields, TemplateProblems } from '../emailAdmin/TemplateParts';
import { DialogButton, EmailBlock, EmailHeader, EmailSpots, FieldHint } from './programAdminStyles';

// The one confirmation dialog for every action with consequences (plan §7.7 item 7): what will
// happen, who gets an email, and whether history is kept. The email part mirrors the real flow - the
// admin sees the exact subject and body the backend previewed, may edit both, and can untick the
// send (e.g. when the invitation went to the wrong person); sending is on by default.
//
// Backend shape it stands for (CancellationNotice): one email per person and action, each with its
// own editable subject and body, listing every spot of theirs the action cancels (`emails` is that
// list, each with the personId the notify message names). `fields` are the inputs an action takes
// instead of (or next to) an email: a plain value, a `select`, a `person` picker whose search
// comes in through `field.search` (and, with `field.create` and `field.findMatches`, offers to create a
// new person), a `textarea` (multi-line text, e.g. a display element's), or a `choice` (radio buttons, `options` [{value, label, detail?,
// disabled?}]). A `required` field without a value keeps the confirm button disabled, and so does a
// field whose `validate(value)` answers false, and so does
// `loading` (the dialog still waits for what it describes) and `confirmDisabled`. A `disabled`
// field is shown but cannot be changed (Remove's refused "Close the gap" box).
//
// `note` is the short text of a dialog that needs no bullet list: one or two sentences under the
// fields, so the choice the dialog is about comes first. While the person picker has its
// "Create a new person" form open the dialog shows nothing but the title and that form (the form
// brings its own one row of buttons): no bullets, no note, no emails, no history, no footer.
//
// `sharedTemplate` is the program change's "tell the affected people" step: ONE editable email for
// every chosen person, with the backend's per-person rendering below it. It carries the
// {{placeholder}} text, its placeholders, the problems that block the send and the two change
// callbacks; the dialog then shows an Edit/Preview switch (two views of one column, the owner's
// layout rule) instead of a subject/body field per person. The person's `changes` ({field, from, to})
// are listed above their email. `secondaryAction` adds the second saving button ("Save without
// telling anyone"), `showCancel` turns the Cancel button off for a result step. `timeRows`
// ({key, title, times, tag, indent}) list what the program time cascade would move, one line each;
// `warnings` are the sentences of the overlaps a change would create, under their own heading. A
// missing `confirmLabel` leaves the primary button out, which the refusal step needs.
//
// Purely presentational: `open`, the text and every value come in as props, every keystroke goes back
// out through a callback, and the actual API call is `onConfirm`, which the owner of the dialog runs
// (a refusal - a 409 from the backend - comes back as `error` and keeps the dialog open).
// The {{placeholder}} list an edited invitation or reminder may use, as help under its textarea: what
// each one is filled with. The label and description come from the backend; the message catalogue
// translates the ones it has (the reminder editor's vocabulary).
const PlaceholderHelp = ({ intl, placeholders }) => {
  if (!placeholders || placeholders.length === 0) return null;
  const text = (p, field) => intl.formatMessage({ id: `inviteReminders.ph.${p.key}.${field}`, defaultMessage: p[field] || '' });
  return (
    <details style={{ margin: '.4rem 0', fontSize: '.74rem', color: '#475569' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
        <FormattedMessage id="programAdmin.confirm.placeholders" defaultMessage="Placeholders you can use" />
      </summary>
      <ul style={{ margin: '.35rem 0 0', paddingLeft: '1.1rem', lineHeight: 1.5 }}>
        {placeholders.map((p) => (
          <li key={p.key}>
            <code>{`{{${p.key}}}`}</code> – {text(p, 'label')}
            {p.description ? `: ${text(p, 'description')}` : ''}
          </li>
        ))}
      </ul>
    </details>
  );
};

const ConfirmDialog = ({
  open, tone = 'default', title, intro, note = null, points = [], pointsTitle = null, emails = [], fields = [], history = null,
  confirmLabel, cancelLabel, error = null, busy = false, loading = false, confirmDisabled = false,
  timeRows = [], timeRowsTitle = null, warnings = [], warningsTitle = null,
  sharedTemplate = null, secondaryAction = null, showCancel = true,
  onToggleSendEmail, onChangeSubject, onChangeBody, onChangeTemplate, onChangeField, onPickPerson,
  onRefreshPreview, onConfirm, onClose,
}) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const [creating, setCreating] = useState(false);
  const [templateView, setTemplateView] = useState('preview');
  useEffect(() => {
    if (!open) {
      setCreating(false);
      setTemplateView('preview');
    }
  }, [open]);
  if (!open) return null;
  const missing = fields.some((field) => (field.required && (field.value == null || field.value === ''))
    || (field.validate && !field.validate(field.value)));

  const fieldControl = (field) => {
    if (field.type === 'person') {
      return (
        <PersonPicker
          value={field.value}
          search={field.search}
          placeholder={field.placeholder}
          label={field.label}
          create={field.create}
          findMatches={field.findMatches}
          onCreatingChange={setCreating}
          onPick={(hit) => onPickPerson?.(field.key, hit)}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <Select
          style={{ marginTop: '.25rem', width: '100%' }}
          value={field.value ?? ''}
          onChange={(e) => onChangeField?.(field.key, e.target.value)}
        >
          {(field.options || []).map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>
      );
    }
    if (field.type === 'textarea') {
      return (
        <Textarea
          style={{ marginTop: '.25rem', width: '100%', fontWeight: 400, boxSizing: 'border-box' }}
          rows={field.rows || 4}
          value={field.value || ''}
          placeholder={field.placeholder}
          onChange={(e) => onChangeField?.(field.key, e.target.value)}
        />
      );
    }
    return (
      <Input
        type={field.type || 'text'}
        style={{ marginTop: '.25rem', width: '100%', fontWeight: 400 }}
        value={field.value || ''}
        placeholder={field.placeholder}
        maxLength={field.maxLength}
        onChange={(e) => onChangeField?.(field.key, e.target.value)}
      />
    );
  };

  return (
    <Overlay onClick={busy ? undefined : onClose}>
      <Modal
        role="dialog"
        aria-label={title || t('programAdmin.confirm.title', 'Confirm')}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: emails.length ? '46rem' : '34rem', maxHeight: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column' }}
      >
        <ModalTitle>
          <AlertTriangle size={18} color={tone === 'danger' ? '#b91c1c' : '#b45309'} />
          {title}
        </ModalTitle>

        <div style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
          {intro && !creating && <p style={{ margin: '0 0 .75rem', fontSize: '.88rem', color: '#334155', lineHeight: 1.55 }}>{intro}</p>}

          {error && (
            <p role="alert" style={{ margin: '0 0 .75rem', padding: '.6rem .7rem', background: '#fee2e2', color: '#991b1b', fontSize: '.82rem', lineHeight: 1.5 }}>
              {error}
            </p>
          )}

          {points.length > 0 && !creating && (
            <>
              <div style={{ fontWeight: 600, fontSize: '.82rem', color: '#334155' }}>
                {pointsTitle || <FormattedMessage id="programAdmin.confirm.willHappen" defaultMessage="This will:" />}
              </div>
              <ModalList>
                {points.map((point, i) => <li key={i}>{point}</li>)}
              </ModalList>
            </>
          )}

          {loading && (
            <p style={{ margin: '.5rem 0', fontSize: '.82rem', color: '#475569' }}>
              <FormattedMessage id="programAdmin.confirm.loading" defaultMessage="Loading…" />
            </p>
          )}

          {(creating ? fields.filter((field) => field.type === 'person') : fields).map((field) => (field.type === 'choice' ? (
            <fieldset key={field.key} style={{ border: 0, padding: 0, margin: '.6rem 0 .25rem' }}>
              <legend style={{ fontSize: '.78rem', fontWeight: 700, color: '#334155', marginBottom: '.35rem' }}>{field.label}</legend>
              {(field.options || []).map((option) => (
                <CheckLabel key={option.value} style={{ margin: '0 0 .4rem', opacity: option.disabled ? 0.6 : 1 }}>
                  <input type="radio" name={field.key} value={option.value} checked={field.value === option.value}
                    disabled={!!option.disabled} onChange={() => onChangeField?.(field.key, option.value)} />
                  <span>
                    <strong>{option.label}</strong>
                    {option.detail && <FieldHint>{option.detail}</FieldHint>}
                  </span>
                </CheckLabel>
              ))}
              {field.hint && <FieldHint>{field.hint}</FieldHint>}
            </fieldset>
          ) : field.type === 'checkbox' ? (
            <label key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '.45rem', fontSize: '.78rem', fontWeight: 700, color: '#334155', margin: '.6rem 0 .25rem' }}>
              <input
                type="checkbox"
                checked={!!field.value}
                disabled={!!field.disabled}
                onChange={(e) => onChangeField?.(field.key, e.target.checked)}
              />
              {field.label}
              {field.hint && <FieldHint>{field.hint}</FieldHint>}
            </label>
          ) : field.type === 'person' ? (
            // Not a <label>: the picker holds several inputs and buttons (and the new-person form).
            <div key={field.key} style={{ fontSize: '.78rem', fontWeight: 700, color: '#334155', margin: '.6rem 0 .25rem' }}>
              <div style={{ marginBottom: '.25rem' }}>{field.label}</div>
              {fieldControl(field)}
              {field.hint && <FieldHint>{field.hint}</FieldHint>}
            </div>
          ) : (
            <label key={field.key} style={{ display: 'block', fontSize: '.78rem', fontWeight: 700, color: '#334155', margin: '.6rem 0 .25rem' }}>
              {field.label}
              {fieldControl(field)}
              {field.hint && <FieldHint>{field.hint}</FieldHint>}
            </label>
          )))}

          {timeRows.length > 0 && !creating && (
            <div style={{ margin: '.6rem 0 .75rem' }}>
              {timeRowsTitle && (
                <div style={{ fontWeight: 700, fontSize: '.82rem', color: '#334155', marginBottom: '.35rem' }}>{timeRowsTitle}</div>
              )}
              <ul data-testid="time-rows"
                style={{ margin: 0, padding: 0, listStyle: 'none', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                {timeRows.map((row) => (
                  <li key={row.key} data-indent={row.indent ? 'true' : undefined}
                    style={{
                      display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '.15rem .5rem',
                      padding: '.4rem .55rem', paddingLeft: row.indent ? '1.6rem' : '.55rem',
                      borderTop: '1px solid #e2e8f0', fontSize: '.8rem', lineHeight: 1.45,
                    }}>
                    <strong style={{ color: '#0f172a', overflowWrap: 'anywhere' }}>{row.title}</strong>
                    <span style={{ color: '#334155', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{row.times}</span>
                    {row.tag && (
                      <span style={{
                        fontSize: '.68rem', fontWeight: 700, color: '#92400e', background: '#fef3c7',
                        padding: '.05rem .4rem', borderRadius: '999px',
                      }}>{row.tag}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {warnings.length > 0 && !creating && (
            <div style={{ margin: '.6rem 0 .75rem', padding: '.55rem .65rem', background: '#fef3c7', color: '#92400e', fontSize: '.8rem', lineHeight: 1.5 }}>
              {warningsTitle && <div style={{ fontWeight: 700 }}>{warningsTitle}</div>}
              <ul style={{ margin: '.25rem 0 0', paddingLeft: '1.1rem' }}>
                {warnings.map((warning, i) => <li key={i}>{warning}</li>)}
              </ul>
            </div>
          )}

          {note && !creating && (
            <p style={{ margin: '.75rem 0 0', fontSize: '.84rem', color: '#475569', lineHeight: 1.5 }}>{note}</p>
          )}

          {sharedTemplate && !creating && (
            <div style={{ margin: '.75rem 0 1rem' }}>
              <EmailViewSwitch view={templateView} onChange={setTemplateView} />
              {templateView === 'edit' && (
                <TemplateFields subject={sharedTemplate.subject || ''} html={sharedTemplate.html || ''}
                  onChangeSubject={sharedTemplate.onChangeSubject} onChangeHtml={sharedTemplate.onChangeHtml}
                  placeholders={sharedTemplate.placeholders || []} />
              )}
              <TemplateProblems problems={sharedTemplate.problems || []}
                titleId="emailAdmin.oneTime.problems" titleDefault="This email can't be sent yet:" />
              <p style={{ margin: '.5rem 0 0', fontSize: '.8rem', color: '#475569', lineHeight: 1.5 }}>
                <FormattedMessage id="emailAdmin.oneTime.onlyThisSend"
                  defaultMessage="This change is for this email only and is not saved; the saved template stays as it is." />
                {sharedTemplate.templateKey && (
                  <>{' '}
                    <Link to={`/admin/emails?tab=emails&key=${sharedTemplate.templateKey}`} style={{ color: '#0369a1', fontWeight: 700 }}>
                      <FormattedMessage id="emailAdmin.oneTime.openTemplate" defaultMessage="Open the saved template" />
                    </Link>
                  </>
                )}
              </p>
              {sharedTemplate.error && (
                <p role="alert" style={{ margin: '.5rem 0 0', padding: '.5rem .6rem', background: '#fee2e2', color: '#991b1b', fontSize: '.78rem' }}>
                  {sharedTemplate.error}
                </p>
              )}
            </div>
          )}

          {!creating && emails.map((email, index) => (
            <EmailBlock key={email.key ?? index} $multi={emails.length > 1}>
              <EmailHeader>
                {email.name}
                {emails.length > 1 && (
                  <> · <FormattedMessage id="programAdmin.confirm.mailOf"
                    defaultMessage="{index, number} of {count}" values={{ index: index + 1, count: emails.length }} /></>
                )}
              </EmailHeader>
              {sharedTemplate && email.hasEmail === false ? (
                <div style={{ fontWeight: 700, color: '#334155', fontSize: '.85rem' }}>
                  <Mail size={13} style={{ verticalAlign: '-2px', marginRight: '.3rem' }} />
                  <FormattedMessage id="programAdmin.confirm.noAddress" defaultMessage="No email address" />
                </div>
              ) : (
                <CheckLabel style={{ fontWeight: 700, color: '#334155' }}>
                  <input type="checkbox" checked={!!email.send} disabled={email.hasEmail === false}
                    onChange={(e) => onToggleSendEmail?.(email.key, e.target.checked)} />
                  <span>
                    <Mail size={13} style={{ verticalAlign: '-2px', marginRight: '.3rem' }} />
                    <FormattedMessage id="programAdmin.confirm.sendEmail" defaultMessage="Send this email to {to}"
                      values={{ to: <strong>{email.to || t('programAdmin.confirm.noEmail', 'no address on file')}</strong> }} />
                  </span>
                </CheckLabel>
              )}
              {!(sharedTemplate && email.hasEmail === false) && (
                <div style={{ fontSize: '.78rem', color: '#475569', margin: '.4rem 0 .6rem' }}>
                  <strong><FormattedMessage id="programAdmin.confirm.to" defaultMessage="To" />:</strong> {email.to || '—'}
                  {email.spots?.length > 0 && (
                    <EmailSpots>
                      <FormattedMessage id="programAdmin.confirm.mailLists"
                        defaultMessage="Lists: {spots}" values={{ spots: email.spots.join(' · ') }} />
                    </EmailSpots>
                  )}
                </div>
              )}
              {email.changes?.length > 0 && (
                <ul style={{ margin: '.4rem 0 .6rem', paddingLeft: '1.1rem', fontSize: '.78rem', color: '#475569', lineHeight: 1.5 }}>
                  {email.changes.map((change, i) => (
                    <li key={`${change.field}-${i}`}>
                      <strong>{t(`programAdmin.confirm.change.field.${change.field}`, change.field)}</strong>{': '}
                      <FormattedMessage id="programAdmin.confirm.changeLine" defaultMessage="was {from}, now {to}"
                        values={{
                          from: change.from == null ? t('programAdmin.confirm.changeNotSet', 'not set') : change.from,
                          to: change.to == null ? t('programAdmin.confirm.changeNotSet', 'not set') : change.to,
                        }} />
                    </li>
                  ))}
                </ul>
              )}
              {!sharedTemplate && (<>
              <label style={{ display: 'block', fontSize: '.78rem', fontWeight: 700, color: '#334155', marginBottom: '.25rem' }}>
                <FormattedMessage id="programAdmin.confirm.subject" defaultMessage="Subject" />
                <Input style={{ marginTop: '.25rem', width: '100%', fontWeight: 400 }}
                  value={(email.template ? email.template.subject : email.subject) || ''}
                  onChange={(e) => (email.template
                    ? onChangeTemplate?.(email.key, { subject: e.target.value })
                    : onChangeSubject?.(email.key, e.target.value))} />
              </label>
              <label style={{ display: 'block', fontSize: '.78rem', fontWeight: 700, color: '#334155', margin: '.6rem 0 .25rem' }}>
                {email.template
                  ? <FormattedMessage id="programAdmin.confirm.editEmail" defaultMessage="Edit email (HTML)" />
                  : <FormattedMessage id="programAdmin.confirm.body" defaultMessage="Email (HTML)" />}
                <Textarea style={{ marginTop: '.25rem', width: '100%', minHeight: '6rem', fontFamily: 'monospace', fontSize: '.74rem', fontWeight: 400 }}
                  value={(email.template ? email.template.html : email.html) || ''}
                  onChange={(e) => (email.template
                    ? onChangeTemplate?.(email.key, { html: e.target.value })
                    : onChangeBody?.(email.key, e.target.value))} />
              </label>
              {email.template && (
                <>
                  <div style={{ fontSize: '.74rem', color: '#475569', margin: '.35rem 0 0' }}>
                    <FormattedMessage id="programAdmin.confirm.editEmailHint"
                      defaultMessage="An invitation and a reminder are edited as templates: keep the {{...}} placeholders – the server fills them in for the person. Unchanged, the built-in/saved text is sent." />
                  </div>
                  <PlaceholderHelp intl={intl} placeholders={email.placeholders} />
                  {email.templateProblems?.length > 0 && (
                    <p role="alert" style={{ margin: '.35rem 0', padding: '.5rem .6rem', background: '#fef3c7', color: '#92400e', fontSize: '.78rem' }}>
                      {email.templateProblems.join(' ')}
                    </p>
                  )}
                  <DialogButton type="button" $secondary disabled={!!email.refreshing} onClick={() => onRefreshPreview?.(email.key)}>
                    {email.refreshing
                      ? <FormattedMessage id="programAdmin.confirm.refreshing" defaultMessage="Refreshing…" />
                      : <FormattedMessage id="programAdmin.confirm.refreshPreview" defaultMessage="Refresh preview" />}
                  </DialogButton>
                  {email.refreshError && (
                    <p role="alert" style={{ margin: '.35rem 0 0', padding: '.5rem .6rem', background: '#fee2e2', color: '#991b1b', fontSize: '.78rem' }}>
                      {email.refreshError}
                    </p>
                  )}
                </>
              )}
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: '#334155', margin: '.6rem 0 .25rem' }}>
                <FormattedMessage id="programAdmin.confirm.preview" defaultMessage="Preview" />
              </div>
              {/* Sandboxed: no scripts, and the sandbox refuses to open any link, so the preview
                  cannot be clicked through. For a notice `previewHtml` is the backend's display
                  variant (images inlined); for an invitation or reminder `renderedHtml` is the
                  backend's rendered email, refreshed here when the template changes. */}
              <iframe
                title={t('programAdmin.confirm.preview', 'Preview')}
                sandbox=""
                srcDoc={`<base target="_blank">${email.previewHtml || email.renderedHtml || email.html || ''}`}
                style={{ width: '100%', height: '10rem', border: '1px solid #e2e8f0', background: '#fff' }}
              />
              </>)}

              {sharedTemplate && templateView === 'preview' && email.send && email.hasEmail !== false && (
                <>
                  <div style={{ fontSize: '.78rem', color: '#475569', margin: '.6rem 0 .25rem', overflowWrap: 'anywhere' }}>
                    <strong><FormattedMessage id="programAdmin.confirm.previewSubject" defaultMessage="Subject" />:</strong> {email.subject || '…'}
                  </div>
                  {/* One rendering per chosen person: what the backend would send them now. */}
                  <iframe
                    title={`${t('programAdmin.confirm.preview', 'Preview')} · ${email.name || email.personId}`}
                    sandbox=""
                    srcDoc={`<base target="_blank">${email.renderedHtml || ''}`}
                    style={{ width: '100%', height: '10rem', border: '1px solid #e2e8f0', background: '#fff' }}
                  />
                </>
              )}
            </EmailBlock>
          ))}

          {history && !creating && (
            <p style={{ display: 'flex', gap: '.4rem', alignItems: 'flex-start', margin: '.75rem 0 0', fontSize: '.82rem', color: history.kept ? '#166534' : '#991b1b', lineHeight: 1.5 }}>
              <History size={14} aria-hidden="true" style={{ flex: 'none', marginTop: '.15rem' }} />
              <span>{history.note}</span>
            </p>
          )}
        </div>

        {!creating && (
          <ModalActions style={{ marginTop: '1rem' }}>
            {showCancel && (
              <DialogButton type="button" $secondary disabled={busy} onClick={onClose}>
                {cancelLabel || <FormattedMessage id="programAdmin.confirm.cancel" defaultMessage="Cancel" />}
              </DialogButton>
            )}
            {secondaryAction && (
              <DialogButton type="button" $secondary disabled={busy || loading || !!secondaryAction.disabled}
                onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </DialogButton>
            )}
            {!!confirmLabel && (
              <DialogButton type="button" $danger={tone === 'danger'} disabled={busy || loading || missing || confirmDisabled} onClick={onConfirm}>
                {busy ? <FormattedMessage id="programAdmin.confirm.working" defaultMessage="Working…" /> : confirmLabel}
              </DialogButton>
            )}
          </ModalActions>
        )}
      </Modal>
    </Overlay>
  );
};

export default ConfirmDialog;
